/** Currencies whose minor unit is not 2 digits. */
const MINOR_DIGITS: Record<string, number> = {
  JPY: 0, KRW: 0, ISK: 0, HUF: 2, CLP: 0, VND: 0, KWD: 3, BHD: 3, JOD: 3, OMR: 3, TND: 3,
};

export function currencyMinorDigits(currency: string): number {
  return MINOR_DIGITS[currency] ?? 2;
}

export function formatMoney(minor: number, currency: string, locale = "en"): string {
  const digits = currencyMinorDigits(currency);
  const value = minor / 10 ** digits;
  const opts = { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits } as const;
  try {
    return new Intl.NumberFormat(locale, opts).format(value);
  } catch {
    try {
      return new Intl.NumberFormat("en", opts).format(value);
    } catch {
      return `${value.toFixed(digits)} ${currency}`;
    }
  }
}

/** Parse a user-entered decimal string ("12,50" / "12.5") into minor units without floating point drift. */
export function parseMoneyToMinor(input: string, currency: string): number | null {
  const digits = currencyMinorDigits(currency);
  const cleaned = input.trim().replace(/[^\d.,-]/g, "");
  if (!cleaned) return null;
  // Use the last separator as the decimal separator.
  const lastSep = Math.max(cleaned.lastIndexOf("."), cleaned.lastIndexOf(","));
  let intPart = cleaned;
  let fracPart = "";
  if (lastSep >= 0 && cleaned.length - lastSep - 1 <= digits && cleaned.length - lastSep - 1 > 0) {
    intPart = cleaned.slice(0, lastSep);
    fracPart = cleaned.slice(lastSep + 1);
  } else if (lastSep >= 0 && cleaned.length - lastSep - 1 === 3 && digits !== 3) {
    // "1.250" is a thousands separator
    intPart = cleaned;
  }
  intPart = intPart.replace(/[.,]/g, "");
  if (!/^-?\d+$/.test(intPart || "0")) return null;
  const frac = (fracPart + "0".repeat(digits)).slice(0, digits);
  const result = Number(intPart || "0") * 10 ** digits + Number(frac || "0");
  if (!Number.isSafeInteger(result) || result < 0) return null;
  return result;
}

export function minorToDecimalString(minor: number, currency: string): string {
  const digits = currencyMinorDigits(currency);
  if (digits === 0) return String(minor);
  const s = String(Math.abs(minor)).padStart(digits + 1, "0");
  return `${minor < 0 ? "-" : ""}${s.slice(0, -digits)}.${s.slice(-digits)}`;
}

/** Tax amount included in a gross price, rounded half-up, in basis points (2000 = 20%). */
export function taxIncluded(grossMinor: number, rateBps: number): number {
  if (rateBps <= 0) return 0;
  return Math.round((grossMinor * rateBps) / (10_000 + rateBps));
}

/** Tax amount to add on top of a net price. */
export function taxExclusive(netMinor: number, rateBps: number): number {
  if (rateBps <= 0) return 0;
  return Math.round((netMinor * rateBps) / 10_000);
}
